import { useRef, useState, useEffect, ChangeEvent, useCallback } from "react"
import { cn } from "../../lib/utils"

interface CounterProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  className?: string
  debounceMs?: number
}

const Counter = ({
  value,
  onChange,
  min = 1,
  max = 999,
  className,
  debounceMs = 300,
}: CounterProps) => {
  const [count, setCount] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedOnChange = useCallback(
    (newValue: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        onChange(newValue)
      }, debounceMs)
    },
    [onChange, debounceMs]
  )

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setCount(value)
  }, [value])

  const handleIncrement = () => {
    if (count < max) {
      const newValue = count + 1
      setCount(newValue)
      debouncedOnChange(newValue)
    }
  }

  const handleDecrement = () => {
    if (count > min) {
      const newValue = count - 1
      setCount(newValue)
      debouncedOnChange(newValue)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    if (inputValue.includes(".") || inputValue.includes("-")) {
      e.preventDefault()
      return
    }

    const numValue = parseInt(inputValue, 10)
    if (!isNaN(numValue) && numValue >= min && numValue <= max) {
      setCount(numValue)
      debouncedOnChange(numValue)
    }
  }

  const handleInputClick = () => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.setSelectionRange(0, count.toString().length)
    }
  }

  const buttonClasses = cn(
    "font-ploni text-foreground flex h-8 w-8 items-center justify-center bg-white border-0 appearance-none text-lg",
    "cursor-pointer",
    "disabled:cursor-not-allowed disabled:ring-0 disabled:ring-inset disabled:ring-gray-200"
  )

  const inputClasses = cn(
    "font-ploni text-foreground h-8 w-12 appearance-none border-l border-r border-solid border-gray-200 bg-gray-50 p-0 m-0",
    "text-center sm:text-lg text-sm font-semibold leading-none",
    "focus:outline-none focus:ring-0"
  )

  return (
    <div
      className={cn(
        "inline-flex h-8 items-center overflow-hidden rounded-full border border-[1px] border-solid border-gray-200 bg-white",
        className
      )}
    >
      <button
        type="button"
        onClick={handleIncrement}
        disabled={count >= max}
        className={buttonClasses}
        data-testid="counter-increment-button"
      >
        +
      </button>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={count}
        onChange={handleChange}
        onClick={handleInputClick}
        onPaste={(e) => e.preventDefault()}
        className={inputClasses}
        data-testid="counter-input"
      />
      <button
        type="button"
        onClick={handleDecrement}
        disabled={count <= min}
        className={buttonClasses}
        data-testid="counter-decrement-button"
      >
        -
      </button>
    </div>
  )
}

export default Counter
